import { useEffect, useState } from "react";
import { X, AlertTriangle, Info, AlertOctagon } from "lucide-react";

/**
 * BroadcastModal — shows the most-recent active urgent message once per session.
 * Dismissal is stored in sessionStorage so the same modal doesn't re-open on every page nav,
 * but comes back the next time the user signs in.
 *
 * Props:
 *  - broadcasts: array from /api/broadcasts/active?audience=... or /api/employee/broadcasts/active
 *  - storageKey: unique key per user context, e.g. "tr_bc_buyer_<buyer_id>" or "tr_bc_emp_<emp_id>"
 */
export default function BroadcastModal({ broadcasts, storageKey }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    if (!broadcasts || broadcasts.length === 0) return;
    const dismissed = JSON.parse(sessionStorage.getItem(storageKey) || "[]");
    // Show the newest broadcast the user has NOT dismissed yet this session.
    const next = broadcasts.find((b) => !dismissed.includes(b.id));
    if (next) {
      setCurrent(next);
      setOpen(true);
    }
  }, [broadcasts, storageKey]);

  if (!open || !current) return null;

  const sev = current.severity || "info";
  const styles = {
    info: { bar: "#1E2C5A", chip: "INFO", icon: Info, tint: "#EEF1FA" },
    warning: { bar: "#C9A968", chip: "URGENT", icon: AlertTriangle, tint: "#FDF8EC" },
    critical: { bar: "#B23A3A", chip: "CRITICAL", icon: AlertOctagon, tint: "#FBECEC" },
  }[sev];
  const Icon = styles.icon;

  const dismiss = () => {
    const dismissed = JSON.parse(sessionStorage.getItem(storageKey) || "[]");
    if (!dismissed.includes(current.id)) dismissed.push(current.id);
    sessionStorage.setItem(storageKey, JSON.stringify(dismissed));
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={dismiss}
      data-testid="broadcast-modal-overlay"
    >
      <div
        className="max-w-lg w-full bg-white shadow-2xl"
        style={{ borderTop: `4px solid ${styles.bar}` }}
        onClick={(e) => e.stopPropagation()}
        data-testid="broadcast-modal"
      >
        <div className="flex items-start justify-between p-6" style={{ background: styles.tint }}>
          <div className="flex items-start gap-3 min-w-0">
            <Icon className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: styles.bar }} strokeWidth={1.5} />
            <div className="min-w-0">
              <p
                className="text-[10px] tracking-[0.25em] uppercase font-medium mb-1"
                style={{ color: styles.bar }}
                data-testid="broadcast-severity"
              >
                {styles.chip}
              </p>
              <h3
                className="font-editorial text-2xl leading-tight text-[#1A1A1A] break-words"
                data-testid="broadcast-title"
              >
                {current.title}
              </h3>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="flex-shrink-0 p-1 hover:bg-black/10 transition-colors"
            aria-label="Close"
            data-testid="broadcast-close"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-6 pt-4">
          <p
            className="text-[15px] leading-relaxed text-[#3A3A3A] whitespace-pre-wrap"
            data-testid="broadcast-body"
          >
            {current.body}
          </p>
          <div className="mt-6 flex items-center justify-end">
            <button
              onClick={dismiss}
              className="px-5 py-2 text-[11px] tracking-[0.2em] uppercase font-medium text-white transition-colors"
              style={{ background: styles.bar }}
              data-testid="broadcast-ack"
            >
              Understood
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
